import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { RaceDetailBody } from "./RaceDetail";
import { ImageUpload } from "@/components/ImageUpload";
import { MultiImageUpload } from "@/components/MultiImageUpload";
import { SportIcon } from "@/components/SportIcon";
import { dateBadge, sportName } from "@/lib/helpers";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ArrowRight, Eye, Save, X, MapPin, Star, CreditCard } from "lucide-react";

const SPORTS = [
  { slug: "running", name: "Running" }, { slug: "trail-running", name: "Trail Running" },
  { slug: "cycling", name: "Cycling" }, { slug: "triathlon", name: "Triathlon" },
  { slug: "duathlon", name: "Duathlon" }, { slug: "fitness-racing", name: "Fitness Racing" },
];
const STATES = ["Kuala Lumpur", "Selangor", "Penang", "Johor", "Perak", "Melaka", "Negeri Sembilan", "Pahang", "Sabah", "Sarawak", "Putrajaya", "Kedah", "Kelantan", "Terengganu", "Perlis", "Labuan", "Other"];
const CATEGORY_PRESETS = ["Men Open", "Women Open", "Men Veteran 40+", "Women Veteran 40+", "Men Veteran 50+", "Women Veteran 50+", "Junior", "Kids", "Corporate"];

const EMPTY = {
  name: "", sport: "", date: "", country: "Malaysia", state: "", city: "", venue: "", start_location: "",
  description_full: "", hero_image: "", organizer_logo: "", gallery: [],
  categories: [{ id: "c0", name: "", distance: "", price: "", start_time: "", description: "" }],
  highlights: { medal: { image_url: "", description: "", images: [] }, tshirt: { image_url: "", description: "", images: [] }, merchandise: [], race_pack: { description: "", image_url: "" } },
  registration_url: "", official_website: "", registration_open_date: "", registration_deadline: "", registration_status: "coming_soon", registration_fee: 0,
  main_start_time: "", briefing_time: "", schedule_info: "", schedule_items: [], additional_info: "",
  distances: [], pricing_periods: [],
  allow_contact: false, contact_whatsapp: "", contact_email: "",
  banner_text_display: "full",
};

// Keep medal/tshirt backward-compatible: normalise to an `images` array on load,
// and mirror images[0] into `image_url` on save so old readers still work.
function normHighlights(h0) {
  const h = { ...EMPTY.highlights, ...(h0 || {}) };
  for (const k of ["medal", "tshirt"]) {
    const m = { ...EMPTY.highlights[k], ...(h[k] || {}) };
    if (!Array.isArray(m.images)) m.images = [];
    if (m.images.length === 0 && m.image_url) m.images = [m.image_url];
    h[k] = m;
  }
  return h;
}
function packHighlights(h0) {
  const h = { ...(h0 || {}) };
  for (const k of ["medal", "tshirt"]) {
    const imgs = ((h[k] || {}).images || []).filter(Boolean);
    h[k] = { ...(h[k] || {}), images: imgs, image_url: imgs[0] || "" };
  }
  return h;
}

function Field({ label, children, required }) {
  return <div><label className="text-sm font-semibold text-slate-700 block mb-1">{label}{required && <span className="text-red-500"> *</span>}</label>{children}</div>;
}
const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2.5 focus:border-[#22d3ee] outline-none text-sm";

export default function OrganizerRaceForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [wantsFeatured, setWantsFeatured] = useState(false);
  const [hasPricing, setHasPricing] = useState(false);
  const [distInput, setDistInput] = useState("");
  useEffect(() => { api.get("/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);

  const stateOptions = (cfg?.states?.[f.country]) || STATES;
  const cy = cfg ? (cfg.currency === "MYR" ? "RM" : cfg.currency + " ") : "RM";
  const listingLabel = cfg ? `${cy}${cfg.listing_price}` : "";
  const listingPromo = cfg?.listing_promo?.active;
  const featuredPrice = cfg ? `${cy}${cfg.featured_price}` : "";
  const featuredPromo = cfg?.featured_promo?.active;
  const slotsFull = !!cfg?.featured_slots_full;
  const availFrom = cfg?.featured_available_from || "";
  const showPricing = hasPricing || (f.pricing_periods || []).some((p) => (p.rows || []).length > 0);

  const addDist = (v) => { const d = (v || "").trim(); if (d && !f.distances.includes(d)) set("distances", [...f.distances, d]); setDistInput(""); };
  const delDist = (i) => set("distances", f.distances.filter((_, idx) => idx !== i));
  const PP_LABELS = ["Early Bird", "Normal", "Late"];
  const getPP = (label) => f.pricing_periods.find((p) => p.label === label) || { label, start_date: "", end_date: "", rows: [] };
  const setPP = (label, next) => set("pricing_periods", [...f.pricing_periods.filter((p) => p.label !== label), next]);
  const updPP = (label, key, val) => setPP(label, { ...getPP(label), [key]: val });
  const addPPRow = (label) => { const p = getPP(label); setPP(label, { ...p, rows: [...(p.rows || []), { distance: "", price: "" }] }); };
  const updPPRow = (label, i, key, val) => { const p = getPP(label); setPP(label, { ...p, rows: (p.rows || []).map((r, idx) => idx === i ? { ...r, [key]: val } : r) }); };
  const delPPRow = (label, i) => { const p = getPP(label); setPP(label, { ...p, rows: (p.rows || []).filter((_, idx) => idx !== i) }); };

  const normPP = (arr) => (arr || []).map((p) => p.rows ? p : ({ label: p.label, start_date: p.start_date || "", end_date: p.end_date || "", rows: (p.price !== "" && p.price != null) ? [{ distance: p.distance || "", price: p.price }] : [] }));

  useEffect(() => {
    if (id) api.get(`/organizer/races/${id}`).then(({ data }) => setF({ ...EMPTY, ...data, pricing_periods: normPP(data.pricing_periods), highlights: normHighlights(data.highlights) })).catch(() => toast.error("Could not load race")).finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setH = (path, v) => setF((s) => { const h = { ...s.highlights }; const [a, b] = path.split("."); h[a] = { ...h[a], [b]: v }; return { ...s, highlights: h }; });

  const addCat = () => set("categories", [...f.categories, { id: `c${f.categories.length}`, name: "", distance: "", price: "", start_time: "", description: "" }]);
  const updCat = (i, k, v) => set("categories", f.categories.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const updCatFields = (i, obj) => set("categories", f.categories.map((c, idx) => idx === i ? { ...c, ...obj } : c));
  const delCat = (i) => set("categories", f.categories.filter((_, idx) => idx !== i));

  const addSched = () => set("schedule_items", [...f.schedule_items, { time: "", label: "" }]);
  const updSched = (i, k, v) => set("schedule_items", f.schedule_items.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const delSched = (i) => set("schedule_items", f.schedule_items.filter((_, idx) => idx !== i));
  void addSched; void updSched; void delSched;

  const payload = () => ({ ...f, highlights: packHighlights(f.highlights), registration_fee: Number(f.registration_fee) || 0,
    categories: f.categories.map((c) => ({ ...c, price: Number(c.price) || 0 })),
    distances: f.distances,
    allow_contact: !!f.allow_contact,
    contact_whatsapp: f.allow_contact ? (f.contact_whatsapp || "") : "",
    contact_email: f.allow_contact ? (f.contact_email || "") : "",
    pricing_periods: (f.pricing_periods || []).map((p) => ({ label: p.label, start_date: p.start_date || "", end_date: p.end_date || "", rows: (p.rows || []).filter((r) => r.price !== "" && r.price != null).map((r) => ({ distance: r.distance || "", price: Number(r.price) || 0 })) })).filter((p) => p.rows.length > 0) });

  const requestFeatured = async (raceId) => {
    try { await api.post(`/organizer/races/${raceId}/feature/request`); toast.success("Featured request submitted — pending admin approval"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit featured request"); }
    nav("/organizer");
  };

  const save = async (goDashboard = true) => {
    if (!f.name || !f.sport || !f.date) { toast.error("Race name, sport and date are required"); return null; }
    setSaving(true);
    try {
      let raceId = id;
      if (id) await api.put(`/organizer/races/${id}`, payload());
      else { const { data } = await api.post("/organizer/races", payload()); raceId = data.id; nav(`/organizer/races/${data.id}/edit`, { replace: true }); }
      toast.success("Draft saved");
      if (wantsFeatured && raceId) { await requestFeatured(raceId); return raceId; }
      if (goDashboard) nav("/organizer");
      return raceId;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); return null; }
    finally { setSaving(false); }
  };

  if (loading) return <Layout><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={32} /></div></Layout>;

  if (preview) return <PreviewModal f={f} onClose={() => setPreview(false)} onSave={() => save(true)} />;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="font-display font-extrabold uppercase text-3xl text-slate-800">{id ? "Edit Race" : "Add New Race"}</h1>
            <p className="text-slate-500 text-sm">Complete the form to list your race on YourRaceFinder.</p>
            {cfg && (
              <p className="text-sm mt-1.5 inline-flex items-center gap-1.5 text-slate-600" data-testid="listing-fee-note">
                <CreditCard size={14} className="text-[#06b6d4]" />
                Calendar Listing Fee:&nbsp;
                {listingPromo
                  ? <span><span className="line-through text-slate-400 mr-1">{cy}{cfg.listing_normal_price}</span><span className="font-bold text-emerald-600">{listingLabel}</span><span className="text-emerald-600 font-semibold"> (promo)</span></span>
                  : <span className="font-bold text-slate-800">{listingLabel}</span>}
                <span className="text-slate-400">· payable after Preview &amp; Submit</span>
              </p>
            )}
            {listingPromo && (cfg?.listing_promo?.start || cfg?.listing_promo?.end) && (
              <p className="text-xs text-emerald-600 mt-0.5">Promotion valid {cfg.listing_promo.start || "now"} → {cfg.listing_promo.end || "until further notice"}</p>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => save(true)} disabled={saving} className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 rounded-lg font-semibold text-sm" data-testid="save-draft-button"><Save size={16} /> Save Draft</button>
            <button onClick={() => setPreview(true)} className="inline-flex items-center gap-2 bg-[#0a1420] text-white px-4 py-2.5 rounded-lg font-semibold text-sm" data-testid="preview-race-button"><Eye size={16} /> Preview</button>
          </div>
        </div>

        <div className="space-y-6">
          {/* BASIC */}
          <Card title="1. Basic Information">
            <Field label="Race Name" required><input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Kuala Lumpur City Run 2026" className={inputCls} data-testid="race-name-input" /></Field>
            <Field label="Full Description"><textarea value={f.description_full} onChange={(e) => set("description_full", e.target.value)} rows={4} placeholder="Tell runners about your event..." className={inputCls} /></Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Sport" required>
                <select value={f.sport} onChange={(e) => set("sport", e.target.value)} className={inputCls} data-testid="race-sport-select">
                  <option value="">Select sport</option>{SPORTS.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Event Date" required><input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={inputCls} data-testid="race-date-input" /></Field>
              <Field label="Country" required>
                <select value={f.country} onChange={(e) => set("country", e.target.value)} className={inputCls}><option>Malaysia</option><option>Singapore</option></select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="State">
                <select value={f.state} onChange={(e) => set("state", e.target.value)} className={inputCls} data-testid="race-state-select" disabled={stateOptions.length === 0}>
                  <option value="">{stateOptions.length === 0 ? "No sub-regions" : "Select state"}</option>{stateOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="City"><input value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="race-city-input" /></Field>
              <Field label="Venue"><input value={f.venue} onChange={(e) => set("venue", e.target.value)} placeholder="Event venue" className={inputCls} /></Field>
            </div>
            <Field label="Start Location"><input value={f.start_location} onChange={(e) => set("start_location", e.target.value)} placeholder="Start line (if different)" className={inputCls} /></Field>
          </Card>

          {/* REGISTRATION */}
          <Card title="2. Registration Information">
            <Field label="Official Race Website (optional)">
              <input value={f.official_website} onChange={(e) => set("official_website", e.target.value)} placeholder="https://your-event-site.com" className={inputCls} data-testid="official-website-input" />
            </Field>
            <p className="text-sm text-slate-500 -mt-2">The event's own website, shown in the Organizer section on your public page. Keep this separate from the Registration link below. If your race only uses a registration platform, leave this empty.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Registration URL"><input value={f.registration_url} onChange={(e) => set("registration_url", e.target.value)} placeholder="https://..." className={inputCls} data-testid="reg-url-input" /></Field>
              <Field label="Registration Status">
                <select value={f.registration_status} onChange={(e) => set("registration_status", e.target.value)} className={inputCls}>
                  <option value="open">Open</option><option value="coming_soon">Coming Soon</option><option value="closed">Closed</option>
                </select>
              </Field>
              <Field label="Registration Opens"><input type="date" value={f.registration_open_date} onChange={(e) => set("registration_open_date", e.target.value)} className={inputCls} /></Field>
              <Field label="Registration Deadline"><input type="date" value={f.registration_deadline} onChange={(e) => set("registration_deadline", e.target.value)} className={inputCls} /></Field>
            </div>
          </Card>

          {/* RACE DISTANCES */}
          <Card title="3. Race Distances">
            <p className="text-sm text-slate-500 -mt-2">Enter the main distances offered in this race. These appear on your public race banner (e.g. 10km &amp; 8km). Duplicates are ignored.</p>
            <div className="flex flex-wrap gap-2 items-center">
              {f.distances.map((d, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-800 font-mono-data text-sm px-2.5 py-1 rounded-lg" data-testid={`distance-chip-${i}`}>{d}<button onClick={() => delDist(i)} className="text-slate-400 hover:text-red-500"><X size={13} /></button></span>
              ))}
              <input value={distInput} onChange={(e) => setDistInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addDist(distInput); } }}
                placeholder="Type a distance, press Enter" className={`${inputCls} w-56`} data-testid="race-distances-input" />
              <button type="button" onClick={() => addDist(distInput)} className="inline-flex items-center gap-1 text-[#06b6d4] font-semibold text-sm"><Plus size={15} /> Add</button>
            </div>
            {f.categories.some((c) => c.distance) && (
              <button type="button" onClick={() => { const uniq = [...new Set(f.categories.map((c) => c.distance).filter(Boolean))]; set("distances", [...new Set([...f.distances, ...uniq])]); }}
                className="text-xs text-slate-500 hover:text-[#06b6d4]">Fill from categories</button>
            )}
          </Card>

          {/* CATEGORIES */}
          <Card title="4. Race Categories" action={<button onClick={addCat} className="inline-flex items-center gap-1 text-[#06b6d4] font-semibold text-sm" data-testid="add-category-button"><Plus size={15} /> Add Category</button>}>
            <div className="space-y-3">
              {f.categories.map((c, i) => {
                const isCustom = c.custom || (!!c.name && !CATEGORY_PRESETS.includes(c.name));
                return (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-start bg-slate-50 p-3 rounded-lg" data-testid={`category-row-${i}`}>
                  <Field label="Category">
                    <select
                      value={isCustom ? "__custom__" : c.name}
                      onChange={(e) => e.target.value === "__custom__" ? updCatFields(i, { custom: true, name: "" }) : updCatFields(i, { custom: false, name: e.target.value })}
                      className={inputCls}
                      data-testid={`category-select-${i}`}
                    >
                      <option value="">Select category</option>
                      {CATEGORY_PRESETS.map((s) => <option key={s} value={s}>{s}</option>)}
                      <option value="__custom__">Custom / Other…</option>
                    </select>
                    {isCustom && <input value={c.name} onChange={(e) => updCat(i, "name", e.target.value)} placeholder="e.g. U25, Team" className={`${inputCls} mt-2`} data-testid={`category-custom-input-${i}`} />}
                  </Field>
                  <Field label="Distance"><input value={c.distance} onChange={(e) => updCat(i, "distance", e.target.value)} placeholder="10K" className={inputCls} /></Field>
                  <Field label="Fee (RM)"><input type="number" value={c.price} onChange={(e) => updCat(i, "price", e.target.value)} placeholder="80" className={inputCls} /></Field>
                  <Field label="Start Time"><input value={c.start_time} onChange={(e) => updCat(i, "start_time", e.target.value)} placeholder="06:30 AM" className={inputCls} /></Field>
                  <div className="flex items-end h-full pb-0.5"><button onClick={() => delCat(i)} className="p-2.5 border border-slate-300 rounded-lg hover:bg-red-50 hover:text-red-500"><Trash2 size={16} /></button></div>
                </div>
                );
              })}
            </div>
          </Card>

          {/* REGISTRATION PRICING PERIODS */}
          <Card title="5. Registration Pricing (Optional)">
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" checked={showPricing} onChange={(e) => { setHasPricing(e.target.checked); if (!e.target.checked) set("pricing_periods", []); }} className="w-4 h-4" data-testid="pricing-periods-toggle" />
              This race has different registration pricing periods
            </label>
            {showPricing && (
              <div className="space-y-4 mt-2">
                {PP_LABELS.map((label) => { const p = getPP(label); const slug = label.replace(/\s+/g, "-").toLowerCase(); return (
                  <div key={label} className="bg-slate-50 p-3 rounded-lg" data-testid={`pricing-period-${slug}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                      <div className="font-semibold text-slate-700 text-sm sm:pb-2.5">{label}</div>
                      <Field label="Start Date"><input type="date" value={p.start_date} onChange={(e) => updPP(label, "start_date", e.target.value)} className={inputCls} /></Field>
                      <Field label="End Date"><input type="date" value={p.end_date} onChange={(e) => updPP(label, "end_date", e.target.value)} className={inputCls} /></Field>
                    </div>
                    <div className="space-y-2 mt-3">
                      {(p.rows || []).map((r, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2 items-end" data-testid={`pricing-row-${slug}-${i}`}>
                          <Field label="Distance"><input value={r.distance} onChange={(e) => updPPRow(label, i, "distance", e.target.value)} placeholder="e.g. 10K" className={inputCls} data-testid={`pricing-distance-${slug}-${i}`} /></Field>
                          <div className="flex gap-2 items-end">
                            <Field label="Price (RM)"><input type="number" value={r.price} onChange={(e) => updPPRow(label, i, "price", e.target.value)} placeholder="70" className={inputCls} data-testid={`pricing-price-${slug}-${i}`} /></Field>
                            <button onClick={() => delPPRow(label, i)} className="p-2.5 border border-slate-300 rounded-lg hover:bg-red-50 hover:text-red-500 mb-0.5"><Trash2 size={15} /></button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={() => addPPRow(label)} className="inline-flex items-center gap-1 text-[#06b6d4] font-semibold text-sm" data-testid={`add-distance-${slug}`}><Plus size={15} /> Add Distance</button>
                    </div>
                  </div>
                ); })}
                <p className="text-xs text-slate-400">Add one or more Distance + Price rows for each period you offer. Periods with no rows are skipped.</p>
              </div>
            )}
          </Card>

          {/* SCHEDULE */}
          <Card title="6. Additional Information (Optional)">
            <Field label="Anything else runners should know?">
              <textarea value={f.additional_info} onChange={(e) => set("additional_info", e.target.value)} rows={8} placeholder="Add any extra details about your race here..." className={inputCls} data-testid="additional-info-input" />
            </Field>
            <p className="text-xs text-slate-400 -mt-2">Examples: race-day programme, warm-up session, prize-giving ceremony, parking information, venue information, special instructions, or other important announcements.</p>
          </Card>

          {/* MEDIA */}
          <Card title="7. Photos & Media">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <ImageUpload label="Main Race Image *" value={f.hero_image} onChange={(v) => set("hero_image", v)} testid="upload-hero" />
              <ImageUpload label="Organizer Logo" value={f.organizer_logo} onChange={(v) => set("organizer_logo", v)} aspect="aspect-square" />
            </div>
            <div className="mt-4">
              <Field label="Banner Text Display">
                <select value={f.banner_text_display || "full"} onChange={(e) => set("banner_text_display", e.target.value)} className={inputCls} data-testid="banner-text-display-select">
                  <option value="full">Full Details — show Race Name + Date + Location + Distances over the banner</option>
                  <option value="title">Title Only — show only the Race Name</option>
                  <option value="none">No Overlay — show the uploaded banner exactly as provided, no text added</option>
                </select>
              </Field>
              <p className="text-xs text-slate-400 mt-2">Choose "No Overlay" if your banner already includes the race name, date and distances. Your structured race details are always kept for search, filters and race cards.</p>
            </div>
            <div className="mt-4">
              <label className="text-sm font-semibold text-slate-700 block mb-2">Gallery Images</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {f.gallery.map((g, i) => (
                  <div key={i} className="relative aspect-video rounded-lg overflow-hidden border border-slate-200">
                    <img src={g} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => set("gallery", f.gallery.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-1"><X size={12} /></button>
                  </div>
                ))}
                <ImageUpload value="" onChange={(v) => set("gallery", [...f.gallery, v])} hint="Add gallery image" />
              </div>
            </div>
          </Card>

          {/* MERCH */}
          <Card title="8. Medal, T-Shirt & Merchandise (Optional)">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <MultiImageUpload label="Finisher Medal Photos" images={f.highlights.medal.images || []} onChange={(arr) => setH("medal.images", arr)} testid="upload-medal" aspect="aspect-square" />
                <textarea value={f.highlights.medal.description} onChange={(e) => setH("medal.description", e.target.value)} placeholder="Describe your finisher medal..." rows={2} className={`${inputCls} mt-2`} />
              </div>
              <div>
                <MultiImageUpload label="Event T-Shirt Photos" images={f.highlights.tshirt.images || []} onChange={(arr) => setH("tshirt.images", arr)} testid="upload-tshirt" aspect="aspect-square" />
                <textarea value={f.highlights.tshirt.description} onChange={(e) => setH("tshirt.description", e.target.value)} placeholder="Describe the event T-shirt..." rows={2} className={`${inputCls} mt-2`} />
              </div>
            </div>
            <Field label="Race Pack Information"><textarea value={f.highlights.race_pack.description} onChange={(e) => setH("race_pack.description", e.target.value)} placeholder="What's in the race pack?" rows={2} className={inputCls} /></Field>
          </Card>

          {/* ORGANIZER CONTACT */}
          <Card title="9. Organizer Contact (Optional)">
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
              <input type="checkbox" checked={f.allow_contact} onChange={(e) => set("allow_contact", e.target.checked)} className="w-4 h-4" data-testid="allow-contact-toggle" />
              Allow runners to contact me directly
            </label>
            {f.allow_contact && (
              <div className="grid sm:grid-cols-2 gap-4 mt-2">
                <Field label="WhatsApp Number"><input value={f.contact_whatsapp} onChange={(e) => set("contact_whatsapp", e.target.value)} placeholder="e.g. +60 12-345 6789" className={inputCls} data-testid="contact-whatsapp-input" /></Field>
                <Field label="Contact Email"><input type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="you@example.com" className={inputCls} data-testid="contact-email-input" /></Field>
                <p className="text-xs text-slate-400 sm:col-span-2">Your number and email are never shown publicly. Runners will see a “WhatsApp Organizer” and an “Email Organizer” button that open the respective app.</p>
              </div>
            )}
          </Card>

          {/* FEATURE THIS RACE */}
          {cfg?.featured_enabled && (
            <Card title="10. Feature Your Race (Optional)">
              {slotsFull ? (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4" data-testid="featured-unavailable-form">
                  <div className="font-display font-bold uppercase text-sm text-amber-700">Featured Race currently unavailable</div>
                  <p className="text-sm text-slate-600 mt-1">All Featured Race slots are currently occupied.</p>
                  {availFrom && <p className="text-sm text-slate-600 mt-0.5">Available again from: <span className="font-semibold">{availFrom}</span></p>}
                </div>
              ) : (
                <label className="flex items-start gap-3 cursor-pointer" data-testid="feature-race-toggle">
                  <input type="checkbox" checked={wantsFeatured} onChange={(e) => setWantsFeatured(e.target.checked)} className="w-4 h-4 mt-1" />
                  <span>
                    <span className="font-semibold text-slate-800 inline-flex items-center gap-1.5"><Star size={15} className="text-amber-500" /> ⭐ Feature Race — {cfg.featured_duration_days || 30} days — {featuredPromo ? <span><span className="line-through text-slate-400">{cy}{cfg.featured_normal_price}</span> <span className="text-emerald-600 font-bold">{featuredPrice}</span></span> : <span className="font-bold">{featuredPrice}</span>}</span>
                    <span className="block text-xs text-amber-600 font-semibold mt-0.5">{cfg.featured_max_slots > 0 ? `Limited availability — ${Math.max(0, cfg.featured_max_slots - (cfg.featured_active_count || 0))} placements available at a time` : "Limited availability — placements open"}</span>
                    <span className="block text-sm text-slate-500 mt-0.5">Give your race extra visibility. Your request will be reviewed by the YourRaceFinder team. Once approved, you'll be notified and shown the amount to pay to activate. You are not charged until you pay after approval.</span>
                  </span>
                </label>
              )}
            </Card>
          )}

          <div className="flex justify-end gap-3 pb-10">
            <button onClick={() => save(true)} disabled={saving} className="border border-slate-300 px-6 py-3 rounded-lg font-semibold">Save Draft</button>
            <button onClick={() => setPreview(true)} className="inline-flex items-center gap-2 bg-[#22d3ee] text-[#0a1420] font-bold px-6 py-3 rounded-lg hover:bg-[#06b6d4]" data-testid="preview-submit-button">Preview & Submit <ArrowRight size={18} /></button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Card({ title, action, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold uppercase tracking-wide text-lg text-slate-800">{title}</h2>{action}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function PreviewModal({ f, onClose, onSave }) {
  const race = {
    ...f,
    country: f.country || "Malaysia",
    description_full: f.description_full || "",
    distances: [...new Set(f.distances || [])],
    gallery: (f.gallery || []).map((g) => g.url || g),
    organizer_name: f.organizer_name || "Your Organization",
    slug: "preview",
    is_demo: false,
  };
  return (
    <Layout>
      <div className="bg-amber-50 border-b border-amber-200 py-3 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-amber-800 font-semibold">Preview — this is exactly how your race page will look after approval</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="inline-flex items-center gap-1 border border-slate-300 bg-white px-4 py-2 rounded-lg text-sm font-semibold"><X size={16} /> Back to Edit</button>
            <button onClick={onSave} className="bg-[#0a1420] text-white font-bold px-4 py-2 rounded-lg text-sm" data-testid="preview-save-button">Save & Return to Dashboard</button>
          </div>
        </div>
      </div>
      <RaceDetailBody race={race} preview />
    </Layout>
  );
}
